import React, { useEffect, useRef, useState } from 'react';

import { Flex, useOutsideClick, Box } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TASK_STATUS_KEYS } from 'screens/home/summary/components/Cards/Initiatives/InitiativesMySummaryCard';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';
import { OneOnOneActionItem } from 'screens/oneOnOneRabbit/interfaces/OneOnOneActionItem';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import { z } from 'zod';

import { sendCompletedOneOnOneInitiativeEvent } from 'constants/mixpanelHelper';

import { showTaskDetails } from 'services/taskDetails';

import { Avatar } from 'uiToolkitV1/Avatar';
import { AvatarGroup } from 'uiToolkitV1/AvatarGroup';
import { Checkbox } from 'uiToolkitV1/Checkbox';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { useUpdateActionItemMutation } from '../hooks/dataHook';

type Props = z.infer<typeof OneOnOneActionItem>;

const InitiativeItem = ({ instanceDataId, toIds, text, taskId, dueDate, detailedStatus, status }: Props) => {
  const loggedInUserId: string = useSelector(selectLoggedInEmployeeId);
  const toIdsArray = toIds?.map(({ id }) => id);
  const isInitiativeAddedByCurrentUser = toIdsArray?.includes(Number(loggedInUserId));
  const isInitiativeEditable = isInitiativeAddedByCurrentUser;
  const history = useHistory();

  const inputRef = useRef<HTMLInputElement>(null);
  const isCompleted = status === 'COMPLETED';

  const { masterId, instanceId } = useOneOnOnePageParams();

  const updateInitiativeMutation = useUpdateActionItemMutation({
    masterId,
    instanceId,
  });

  const [initiativeText, setInitiativeText] = useState(text);

  const [isInitiativeCompleted, setIsInitiativeCompleted] = useState(isCompleted);

  const [isInitiativeError, setIsInitiativeError] = useState(false);

  const updateInitiativeHandler = () => {
    if (initiativeText === text && isInitiativeCompleted === isCompleted) {
      return;
    }
    if (!initiativeText || initiativeText.trim() === '') {
      setIsInitiativeError(true);
      inputRef.current?.focus();
      return;
    }
    try {
      updateInitiativeMutation.mutate({
        updatedActionItem: {
          text: initiativeText,
          taskId,
          instanceDataId,
          status: isInitiativeCompleted ? 'COMPLETED' : 'ACTIVE',
          detailedStatus,
          dueDate,
          toIds,
        },
      });
    } catch (error) {
      console.log('Error updating initiative : ', error);
    } finally {
      setIsInitiativeError(false);
    }
  };

  useEffect(() => {
    setInitiativeText(text);
    setIsInitiativeCompleted(isCompleted);
  }, [text, isCompleted]);

  const dispatch = useDispatch();

  const openInitiativeSidepanel = () => {
    dispatch(
      // @ts-expect-error:expected
      showTaskDetails({
        taskIds: [taskId],
        onDelete: () => {},
        onUpdate: () => {},
        employee: {},
        type: 'TASK',
        history,
      })
    );
  };

  const stopPropagationOnClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const initiativeTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setInitiativeText(e.target.value);
  };

  const onEnterPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      updateInitiativeHandler();
      if (!initiativeText || initiativeText.trim() === '') {
        inputRef.current?.focus();
      } else {
        inputRef.current?.blur();
      }
    }
  };

  useOutsideClick({
    ref: inputRef,
    handler: () => updateInitiativeHandler(),
  });

  const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    try {
      setIsInitiativeCompleted(e.target.checked);
      updateInitiativeMutation.mutate({
        updatedActionItem: {
          text: initiativeText,
          taskId,
          instanceDataId,
          status: e.target.checked ? 'COMPLETED' : 'ACTIVE',
          detailedStatus,
          dueDate,
          toIds,
        },
      });

      sendCompletedOneOnOneInitiativeEvent({
        eventProperties: {
          instance_id: instanceId,
        },
      });
    } catch (error) {
      console.log('Error updating initiative : ', error);
    }
  };

  return (
    <Flex
      sx={{
        ':hover': {
          backgroundColor: '#F6FAFE',
        },
      }}
      p="18px 16px"
      alignItems="center"
      w="100%"
      height="115px"
      onClick={openInitiativeSidepanel}
      cursor="pointer"
    >
      <Flex alignItems="flex-start" w="100%">
        {/* REFER: https://github.com/chakra-ui/chakra-ui/issues/2854#issuecomment-749810560 */}
        <Box onClick={stopPropagationOnClick}>
          <Checkbox
            mt="12px"
            isChecked={isInitiativeCompleted}
            // @ts-expect-error:expected
            onChange={onCheckboxChange}
            data-testid="initiative-checkbox"
          />
        </Box>
        <Flex flexDir="column" w="100%">
          <Flex alignItems="center" w="100%">
            {isInitiativeEditable ? (
              <InputV2
                ref={inputRef}
                isDashed
                value={initiativeText}
                onClick={stopPropagationOnClick}
                onChange={initiativeTextChange}
                onKeyDown={onEnterPress}
                isInvalid={isInitiativeError}
                sx={{
                  textDecoration: isCompleted ? 'line-through' : 'none',
                  fontWeight: '600',
                }}
                isReadOnly={isCompleted}
              />
            ) : (
              <TextBodyMedium
                sx={{
                  textDecoration: isCompleted ? 'line-through' : 'none',
                  fontWeight: '600',
                }}
                w="100%"
              >
                {initiativeText}
              </TextBodyMedium>
            )}
          </Flex>
          <Flex mt="12px" alignItems="center">
            <Flex minHeight="28px" alignItems="center">
              <TextLabel color={TASK_STATUS_KEYS[detailedStatus]?.color} bold data-testid="initiative-status">
                {TASK_STATUS_KEYS[detailedStatus]?.label}
              </TextLabel>

              {/* <DateComp
                date={Date.now()}
                isRelativeDateFormat
                enableRelativeFormatDistanceColor
                textComponent={DateText}
                forceRelativeDateFormat
                showDateInBrackets
              /> */}
            </Flex>

            <AvatarGroup ml="auto">
              {toIds?.map((user, id) => {
                const { displayName, profilePhoto, ownerType } = user;
                return (
                  <Avatar
                    sx={{ border: ownerType === 'OWNER' ? '1px solid #60C861 !important' : '0' }}
                    name={displayName}
                    src={profilePhoto}
                    // eslint-disable-next-line react/no-array-index-key
                    key={id}
                  />
                );
              })}
            </AvatarGroup>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export { InitiativeItem };
